import React, { Fragment, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { getContent, patchContent, postContent } from "../../../helpers/FetchContent";
import SpinnerSmall from "../../SpinnerSmall";
import ErrorMsgComponent from "../../../helpers/Errors";
import { displayApiError } from "../../../helpers/DisplayAPIError";
import { useVideoStore } from "../../../stores/video-store";
import { videoCreateInitialData, videoUpdateInitialData } from "../../../constants/inits";
import { getTitle } from "../../../helpers/Common";

export default function CreateContentVideo(props) {
	const setVideos = useVideoStore(state => state.setVideos);

	const [errMsg, setErrMsg] = useState("");
	const [loading, setLoading] = useState(false);
	const cancelButtonRef = useRef(null);
	const [formData, updateFormData] = useState(props.isEdit ? videoCreateInitialData : videoUpdateInitialData);
	const [formErrors, setFormErrors] = useState({});
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [video, setVideo] = useState();

	const validateForm = () => {
		let errors = {};

		if (formData.title_en <= 0) {
			errors.title_en = "English title required";
		}
		if (formData.title_de <= 0) {
			errors.title_de = "German title required";
		}
		if (formData.url <= 0) {
			errors.url = "URL required";
		}
		if (formData.order <= 0 || isNaN(formData.order)) {
			errors.order = "Order should be a valid number";
		}

		return errors;
	};

	const handleChange = e => {
		updateFormData({
			...formData,
			[e.target.name]: e.target.value
		});
	};

	const getVideos = async () => {
		await getContent(`${process.env.REACT_APP_NEXT_PUBLIC_API_URL}/v1/content_videos/`, setVideos);
	};

	const getVideo = async () => {
		await getContent(`${process.env.REACT_APP_NEXT_PUBLIC_API_URL}/v1/content_videos/${props.video_id}`, setVideo);
	};

	const videoToFormData = () => {
		updateFormData({
			title_en: getTitle(video.titles, "en"),
			title_de: getTitle(video.titles, "de"),
			url: video.url,
			order: video.order
		});
	};

	const handleSubmit = async e => {
		e.preventDefault();
		setLoading(true);
		setFormErrors({});

		const errors = await validateForm();
		setIsSubmitted(true);

		if (Object.keys(errors).length === 0) {
			let body = {
				titles: [
					{
						language: "en",
						title: formData.title_en
					},
					{
						language: "de",
						title: formData.title_de
					}
				],
				order: Number(formData.order),
				url: formData.url
			};

			let response = null;
			if (props.isEdit) {
				body.id = await props.video_id;

				response = await patchContent(process.env.REACT_APP_NEXT_PUBLIC_API_URL + "/v1/content_videos/", body);
			} else {
				response = await postContent(process.env.REACT_APP_NEXT_PUBLIC_API_URL + "/v1/content_videos/", body);
			}

			if (response.ok) {
				// Refresh list upon successful creation
				await getVideos();
				props.onClose();
			} else {
				// Show endpoint error if exists
				const err = await response.json();
				setErrMsg(
					err.error ? displayApiError(err.error) : `Unable to ${props.edit ? "update" : " create"} plan`
				);
			}
		} else {
			setFormErrors(errors);
		}

		setLoading(false);
	};

	useEffect(() => {
		if (props.open) {
			updateFormData(props.isEdit ? videoUpdateInitialData : videoCreateInitialData);

			if (props.isEdit) {
				getVideo();
			}
		}
	}, [props.open]);

	useEffect(() => {
		if (video) {
			videoToFormData();
		}
	}, [video]);

	const resetForm = () => {
		props.onClose();
		setFormErrors({});
	};

	return (
		<>
			<Transition.Root show={props.open} as={Fragment}>
				<Dialog
					as="div"
					className="fixed z-10 inset-0 overflow-y-auto"
					initialFocus={cancelButtonRef}
					onClose={resetForm}
				>
					<div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
						<Transition.Child
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0"
							enterTo="opacity-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100"
							leaveTo="opacity-0"
						>
							<Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
						</Transition.Child>

						{/* This element is to trick the browser into centering the modal contents. */}
						<span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
							&#8203;
						</span>
						<Transition.Child
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
							enterTo="opacity-100 translate-y-0 sm:scale-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100 translate-y-0 sm:scale-100"
							leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
						>
							<div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all md:my-8 md:align-middle md:max-w-6xl md:w-full">
								<div className="bg-white px-4 pt-5 pb-4 md:p-16 md:pb-14">
									<div className="lg:flex lg:items-start">
										<div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
											<Dialog.Title
												as="h3"
												className="text-lg leading-6 font-medium text-gray-900"
											>
												{props.isEdit ? "Update" : "Create"} Video
											</Dialog.Title>
											<div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
												<div className="md:grid md:grid-cols-3 md:gap-6">
													<div className="md:col-span-1">
														<p className="mt-1 text-sm text-gray-500">
															{props.isEdit
																? "Please fill in the necessary information to update your Video"
																: "Please fill in the necessary information to create your new Video"}
														</p>
													</div>
													<div className="mt-5 md:mt-0 md:col-span-2">
														<form action="#" method="POST">
															<div className="grid grid-cols-6 gap-6">
																<div className="col-span-3">
																	<label
																		htmlFor="title_en"
																		className="block text-sm font-medium text-gray-700"
																	>
																		English Title
																	</label>
																	<input
																		type="text"
																		name="title_en"
																		id="title_en"
																		value={formData.title_en}
																		onChange={handleChange}
																		className={`"mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md ${isSubmitted && formErrors.title_en && "border-red-700"}`}
																	/>
																	{isSubmitted && formErrors.title_en && (
																		<span className="text-red-700 text-sm">
																			{formErrors.title_en}
																		</span>
																	)}
																</div>
																<div className="col-span-3">
																	<label
																		htmlFor="title_de"
																		className="block text-sm font-medium text-gray-700"
																	>
																		German Title
																	</label>
																	<input
																		type="text"
																		name="title_de"
																		value={formData.title_de}
																		id="title_de"
																		onChange={handleChange}
																		className={`"mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md ${isSubmitted && formErrors.title_de && "border-red-700"}`}
																	/>
																	{isSubmitted && formErrors.title_de && (
																		<span className="text-red-700 text-sm">
																			{formErrors.title_de}
																		</span>
																	)}
																</div>
																<div className="col-span-3">
																	<label
																		htmlFor="url"
																		className="block text-sm font-medium text-gray-700"
																	>
																		URL
																	</label>
																	<input
																		type="text"
																		name="url"
																		id="url"
																		value={formData.url}
																		onChange={handleChange}
																		className={`"mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md ${isSubmitted && formErrors.url && "border-red-700"}`}
																	/>
																	{isSubmitted && formErrors.url && (
																		<span className="text-red-700 text-sm">
																			{formErrors.url}
																		</span>
																	)}
																</div>
																<div className="col-span-3">
																	<label
																		htmlFor="Order"
																		className="block text-sm font-medium text-gray-700"
																	>
																		Order
																	</label>
																	<input
																		type="text"
																		name="order"
																		id="order"
																		value={formData.order}
																		onChange={handleChange}
																		className={`"mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md ${isSubmitted && formErrors.order && "border-red-700"}`}
																	/>
																	{isSubmitted && formErrors.order && (
																		<span className="text-red-700 text-sm">
																			{formErrors.order}
																		</span>
																	)}
																</div>
															</div>
														</form>
														{errMsg && <ErrorMsgComponent errMsg={errMsg} />}
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
									<button
										type="button"
										className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm"
										onClick={handleSubmit}
									>
										{loading && <SpinnerSmall />}
										{!loading && props.isEdit ? "Update" : "Create"}
									</button>
									<button
										type="button"
										className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
										onClick={resetForm}
										ref={cancelButtonRef}
									>
										Cancel
									</button>
								</div>
							</div>
						</Transition.Child>
					</div>
				</Dialog>
			</Transition.Root>
		</>
	);
}
